import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import {
  FaWhatsapp,
  FaEnvelope,
  FaMapMarkerAlt,
  FaHeadset,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationCircle,
} from 'react-icons/fa';
import { APP_CONFIG } from '../config/app.config';
import './Support.css';

const { service, contact_template, publicKey } = APP_CONFIG.emailJs;

// ---------------------------------------------------------------------------
// Validate that every required field in the FormData is non-empty.
// HTML5 `required` attributes already guard the browser path, but this
// provides a safety net for programmatic / test submissions.
// ---------------------------------------------------------------------------
const validateForm = (data) => {
  return (
    data.get('name')?.trim() &&
    data.get('email')?.trim() &&
    data.get('service')?.trim() &&
    data.get('message')?.trim()
  );
};

const Support = ({ org }) => {
  const formRef = useRef();                    // EmailJS reads the DOM form directly
  const [formSubmitted, setFormSubmitted] = useState(false);   // disables the button while sending
  const [submitStatus,  setSubmitStatus]  = useState(null);    // 'success' | null
  const [submitError,   setSubmitError]   = useState(null);    // error message string | null

  // -------------------------------------------------------------------------
  // handleSubmit
  // -------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Secondary validation (belt-and-suspenders alongside HTML5 `required`)
    const data = new FormData(formRef.current);
    if (!validateForm(data)) {
      setSubmitError('Please fill in all required fields before sending.');
      return;
    }

    setFormSubmitted(true);   // disable button / show spinner

    try {
      await emailjs.sendForm(
        service,
        contact_template,
        formRef.current,
        publicKey,
      );

      setSubmitStatus('success');
      formRef.current.reset();
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitError(
        error?.text ||
        error?.message ||
        'Unable to send your message. Please try again.',
      );
    } finally {
      setFormSubmitted(false);   // re-enable button
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="op-support container-fluid py-3 py-md-4 px-3 px-md-4">

      <div className="mb-4">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="op-support-header-icon"><FaHeadset size={18} /></span>
          <h2 className="op-page-title mb-0">Help &amp; Support</h2>
        </div>
        <p className="op-subtitle mb-0">
          Get in touch with our team for payroll assistance, system issues, or general inquiries.
        </p>
      </div>

      <div className="row g-3 g-md-4">
        {/* ------------------------------------------------------------------ */}
        {/* Left Column: Contact Form                                          */}
        {/* ------------------------------------------------------------------ */}
        <div className="col-lg-7">
          <div className="op-card h-100 p-3 p-md-4">
            <h5 className="op-section-title mb-4">Send us a Message</h5>

            {/* Success banner */}
            {submitStatus === 'success' && (
              <div className="op-alert-success mb-3">
                <FaCheckCircle />
                Your message has been sent successfully. Our support team will respond shortly.
              </div>
            )}

            {/* Error banner */}
            {submitError && (
              <div className="op-alert-error mb-3">
                <FaExclamationCircle />
                {submitError}
              </div>
            )}

            {/* ------------------------------------------------------------- */}
            {/* Contact form — formRef lets EmailJS read field values directly */}
            {/* ------------------------------------------------------------- */}
            <form ref={formRef} className="op-contact-form" onSubmit={handleSubmit}>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="op-form-control"
                    name="name"
                    placeholder="Name"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <input
                    type="email"
                    className="op-form-control"
                    name="email"
                    placeholder="Email"
                    required
                  />
                </div>
              </div>

              <div className="mb-3">
                <select
                  className="op-form-control op-form-select"
                  name="service"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>Select a service</option>
                  <option value="Employee Management">Employee Management</option>
                  <option value="Payroll Periods">Payroll Periods</option>
                  <option value="Payroll Records">Payroll Records</option>
                  <option value="Payroll Reports">Payroll Reports</option>
                  <option value="Bulk Export">Bulk Export</option>
                  <option value="Account & Access">Account &amp; Access</option>
                  <option value="Billing">Billing</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="mb-4">
                <textarea
                  className="op-form-control"
                  name="message"
                  placeholder="Message"
                  rows="5"
                  required
                />
              </div>

              {/*
                type="submit"  → triggers onSubmit={handleSubmit} on the form
                disabled       → prevents double-clicks while EmailJS is sending
              */}
              <button
                type="submit"
                className="btn op-btn-primary px-4 d-inline-flex align-items-center gap-2"
                disabled={formSubmitted}
              >
                {formSubmitted ? (
                  <><span className="op-btn-spinner" /> Sending…</>
                ) : (
                  <><FaPaperPlane /> Send Message</>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* Right Column: Direct Contact Info                                  */}
        {/* ------------------------------------------------------------------ */}
        <div className="col-lg-5">
          <div className="op-card p-3 p-md-4">
            <h5 className="op-section-title mb-4">Direct Contact</h5>

            {/* WhatsApp Highlight */}
            <div className="op-whatsapp-card mb-4">
              <div className="d-flex align-items-center gap-3 mb-2">
                <FaWhatsapp size={28} />
                <h6 className="fw-bold mb-0">Chat on WhatsApp</h6>
              </div>
              <p className="mb-3">Fastest response times for quick queries and support tracking.</p>
              <a
                href={APP_CONFIG.whatsAppLink}
                target="_blank"
                rel="noopener noreferrer"
                className="op-whatsapp-btn"
              >
                Message Us Now
              </a>
            </div>

            <div className="op-snapshot-row">
              <div className="op-snapshot-icon"><FaEnvelope size={14} /></div>
              <div className="flex-grow-1">
                <div className="op-snapshot-label">Email Support</div>
                <a href={APP_CONFIG.emailLink} className="op-snapshot-value">{APP_CONFIG.email}</a>
                <div className="op-support-meta">Mon–Fri, 9am – 5pm EST</div>
              </div>
            </div>

            <div className="op-snapshot-row">
              <div className="op-snapshot-icon"><FaMapMarkerAlt size={14} /></div>
              <div className="flex-grow-1">
                <div className="op-snapshot-label">Office Location</div>
                <a href={APP_CONFIG.addressLink} target="_blank" rel="noopener noreferrer" className="op-snapshot-value">
                  {APP_CONFIG.address}
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Support;